// Florida New Business Report — public, citable digital-PR page at
// /florida-new-business-report. Auto-generated from live data (30-day window).

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { theme } from './theme';
import { setPageSeo } from './seo';

const API = process.env.REACT_APP_API_URL || 'https://api.leadengine.amanar.ai';
const fmt = (n) => (n == null ? '—' : n.toLocaleString('en-US'));

export default function MonthlyReport() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/v1/public/monthly-report`)
      .then((r) => r.json()).then(setData).catch(() => setError(true));
  }, []);

  useEffect(() => {
    const month = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
    setPageSeo({
      title: `Florida New Business Report — ${month} | Amanar`,
      description: data
        ? `${fmt(data.total)} new businesses registered in Florida in the last 30 days${data.change_pct != null ? `, ${data.change_pct >= 0 ? 'up' : 'down'} ${Math.abs(data.change_pct)}% vs the prior month` : ''}. Free monthly data on new Florida business formation by industry and city.`
        : 'Monthly data on new Florida business formation — registrations, industries, and top cities. Updated daily from state records.',
      canonical: 'https://leadengine.amanar.ai/florida-new-business-report',
    });
  }, [data]);

  const s = {
    page: { background: theme.bg, minHeight: '100vh', color: theme.text,
      fontFamily: 'system-ui, -apple-system, sans-serif' },
    wrap: { maxWidth: 820, margin: '0 auto', padding: '32px 22px 80px' },
    brand: { color: theme.primary, fontWeight: 800, letterSpacing: 2, fontSize: '0.8rem', textDecoration: 'none' },
    h1: { color: theme.heading, fontSize: 'clamp(1.9rem, 4vw, 2.6rem)', margin: '12px 0 6px', letterSpacing: '-0.8px', lineHeight: 1.1 },
    sub: { color: theme.textMute, fontSize: '0.95rem', margin: 0 },
    heroCard: { background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 16, padding: '28px', margin: '28px 0' },
    bigNum: { fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 'clamp(2.6rem, 7vw, 4rem)', fontWeight: 800, color: theme.heading, letterSpacing: '-2px', lineHeight: 1 },
    trend: (up) => ({ display: 'inline-block', marginLeft: 14, padding: '4px 12px', borderRadius: 999, fontSize: '0.95rem', fontWeight: 700,
      color: up ? '#D1FAE5' : '#FEE2E2', background: up ? '#065F46' : '#7F1D1D', verticalAlign: 'middle' }),
    kicker: { fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem', letterSpacing: 1.5, textTransform: 'uppercase', color: theme.primary, margin: '34px 0 12px' },
    h2: { color: theme.heading, fontSize: '1.35rem', margin: '0 0 14px', letterSpacing: '-0.3px' },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 },
    panel: { background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 18 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${theme.border}` },
    num: { fontFamily: 'ui-monospace, monospace', color: theme.heading, fontWeight: 700 },
    cite: { background: theme.surfaceAlt, borderRadius: 10, padding: '14px 16px', fontSize: '0.82rem', color: theme.text, margin: '24px 0', lineHeight: 1.55 },
    cta: { display: 'inline-block', marginTop: 8, padding: '12px 22px', background: theme.primary, color: '#fff', borderRadius: 9, fontWeight: 700, textDecoration: 'none' },
    foot: { borderTop: `1px solid ${theme.border}`, marginTop: 40, paddingTop: 18, color: theme.textMute, fontSize: '0.78rem' },
  };

  const bar = (count, max) => (
    <div style={{ height: 6, background: theme.surfaceAlt, borderRadius: 3, marginTop: 5, overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(4, (count / max) * 100)}%`, height: '100%', background: theme.primary }} />
    </div>
  );

  if (error) return <div style={s.page}><div style={s.wrap}><p>Report temporarily unavailable.</p></div></div>;
  if (!data) return <div style={s.page}><div style={s.wrap}><p style={{ color: theme.textMute, padding: '40px 0' }}>Loading the latest figures…</p></div></div>;

  const up = (data.change_pct ?? 0) >= 0;
  const monthYear = new Date(data.generated_at).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const asOf = new Date(data.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const catMax = Math.max(...(data.by_category.map((c) => c.count)), 1);
  const cityMax = Math.max(...(data.top_cities.map((c) => c.count)), 1);

  return (
    <div style={s.page}>
      <div style={s.wrap}>
        <Link to="/" style={s.brand}>AMANAR LEAD ENGINE</Link>
        <h1 style={s.h1}>Florida New Business Report</h1>
        <p style={s.sub}>{monthYear} · trailing 30 days · updated daily · as of {asOf}</p>

        <div style={s.heroCard}>
          <div style={{ color: theme.textMute, fontSize: '0.85rem', marginBottom: 10 }}>New business registrations in Florida, last 30 days</div>
          <div>
            <span style={s.bigNum}>{fmt(data.total)}</span>
            {data.change_pct != null && (
              <span style={s.trend(up)}>{up ? '▲' : '▼'} {Math.abs(data.change_pct)}% vs prior 30 days</span>
            )}
          </div>
          <div style={{ color: theme.textMute, fontSize: '0.9rem', marginTop: 12 }}>
            That's about <b style={{ color: theme.text }}>{fmt(data.per_day)} new businesses a day</b> registering across the state.
          </div>
        </div>

        <div style={s.kicker}>New licensed businesses by industry</div>
        <h2 style={s.h2}>What's opening across Florida</h2>
        <div style={s.panel}>
          {data.by_category.map((c) => (
            <div key={c.key} style={{ padding: '9px 0', borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{c.label}</span><span style={s.num}>{fmt(c.count)}</span>
              </div>
              {bar(c.count, catMax)}
            </div>
          ))}
        </div>

        <div style={s.kicker}>Where the growth is</div>
        <h2 style={s.h2}>Top cities & counties</h2>
        <div style={s.grid2}>
          <div style={s.panel}>
            <div style={{ color: theme.textMute, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Cities</div>
            {data.top_cities.map((c) => (
              <div key={c.city}>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8 }}>
                  <span>{c.city}</span><span style={s.num}>{fmt(c.count)}</span>
                </div>
                {bar(c.count, cityMax)}
              </div>
            ))}
          </div>
          <div style={s.panel}>
            <div style={{ color: theme.textMute, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Counties</div>
            {data.top_counties.map((c) => (
              <div key={c.county} style={s.row}><span>{c.county}</span><span style={s.num}>{fmt(c.count)}</span></div>
            ))}
          </div>
        </div>

        <div style={s.cite}>
          <b style={{ color: theme.heading }}>Cite this report:</b> "Florida New Business Report,
          Amanar ({asOf}). https://leadengine.amanar.ai/florida-new-business-report"<br />
          <span style={{ color: theme.textMute }}>{data.methodology}</span>
        </div>

        <div style={{ margin: '28px 0' }}>
          <h2 style={s.h2}>Reach these businesses first</h2>
          <p style={{ color: theme.text, fontSize: '0.95rem', maxWidth: '60ch' }}>
            Amanar Lead Engine tracks every new Florida business daily — with contact info, category, and location.
            Browse the live data by city and category.
          </p>
          <Link to="/new/food" style={s.cta}>Explore live listings →</Link>
        </div>

        <div style={s.foot}>
          © {new Date().getFullYear()} Amanar · Compiled from Florida DBPR &amp; Division of Corporations public records ·
          <Link to="/" style={{ color: theme.primary }}> Amanar Lead Engine</Link>
        </div>
      </div>
    </div>
  );
}
